import clsBaseGlobal from './base'
import {settings as api} from '@app/api'

/**
 * Datatable settings. 
 * Although datatable settings are global settings like other globals, the internals are a bit different.  
 * 
 * The reason is historic. This is how the datatable component was built.
 * The class stores the datatable configurations in a store where each datatable has an entry in. 
 * 
 * A clone is not necesary for this object. This very object is used to get the columns (getColumns) and to 
 * save the columns for a specific datatable (save).
 * 
 * The datatable configuration is saved in the database as a stringified json object. 
 * Of course, in here, we add the unserialized json objects in the store.
 * 
 */

class clsGlobalDatatable extends clsBaseGlobal {    
    store = {};

    #resolveFilled;
    #pFilled = new Promise((resolve, reject) => {
        this.#resolveFilled = resolve;
    })

    // Use promiseFilled to await until the data table global is filled. 
    // As datatables may be used in modal dialogs, the datatable settings may not be loaded at the time of
    // datatable initialization. In that case, the datatable should apply the settings when loaded.
    get promiseFilled() { return this.#pFilled;}

    // We do not utilize the fillable mechanism of the base class.
    get fillable() { return ["dummy"]; }

    /**
     * When parsing to and from json, the names of fields are escaped when containing e.g. a .
     * Just filter them out. 
     * @param {*} table 
     * @returns 
     */
    sanitizeTablename(table) {
        return `${table}`.replaceAll(".", "_");
    }

    fill(arrData) {
        var self = this;
        
        // When the data is no array, clear the store. 
        // This is common when the user did not store any database setting yet.
        if (!Array.isArray(arrData)) {
            console.log("No datatable settings");
            arrData = [];
            this.store = {};
            // ... and just proceed so that #pFilled is resolved.
        }   

        // Fill the store with the available datatable configurations.
        for(var n = 0; n < arrData.length; n++) {
            let tableSetting = arrData[n];
            tableSetting.datatable = this.sanitizeTablename(tableSetting.datatable);
            if (tableSetting && tableSetting.datatable) {
                try {
                    let config = JSON.parse(tableSetting.config);
                    if (config) {
                        self.store[tableSetting.datatable] = config; // array
                    }
                }
                catch(e) {
                    console.error(`Parsing dt setting config ${tableSetting.datatable}`, e);
                }
            }
        }

        // Resolve the filled promise.
        this.#resolveFilled();
    }
    
    /**
     * Save a table configuration
     * We override the base implementation as this works per table, not per 'total datatable settings'.
     * Also, this is called directly by the datatable component.
     * 
     * @param {*} table 
     * @param {*} columns 
     */
    async save(table, columns, extraconfig) {

        table = this.sanitizeTablename(table);

        var saveColumns = [];
        columns.forEach( (col) => {
            var sCol = {value: col.value, visible: col.visible};
            if (col.sort) {
                // sort may be a referece object. Flat it down to a string.
                if (col.sort.value && typeof(col.sort.value) == "string") {
                    sCol.sort = col.sort.value;
                } else if (typeof(col.sort) == "string"){
                    sCol.sort = col.sort
                }
            }
            saveColumns.push(sCol);
        })
        var config = {columns: saveColumns};
        for (var key in (extraconfig||{})) {
            config[key] = extraconfig[key];
        }
//        console.log('config', config)
        let str = JSON.stringify(config);
//        let parsed = JSON.parse(str);
//        console.log('save dt settings original, str, parsed: ', {datatable: table, config: str})
        await api.save("datatable", {datatable: table, config: str});
        this.store[table] = config;
    }

    /**
     * Get the columns for a table.
     * @param {} table 
     * @returns 
     */
    getColumns(table) {

        table = this.sanitizeTablename(table);

        let config = this.store[table] || {};
        if (!config || !config.columns ) {
            return [];
        }
        return config.columns;
    }
    /**
     * Get the columns as a named object for easier searching.
     * @param {} table - the tablename 
     * @returns {cmd: {...}, col2: {...}} - the columns as a named object. The key is the column value.
     */
    getColumnsAsNamedObject(table) { 
        var arrConfig = this.getColumns(table);
        if (!arrConfig || !arrConfig.length) {
            return null;
        }
        // Convert the configuration to an associative array for easier searching and add an order index as well.
        var config = {};
        for(var n = 0; n < arrConfig.length; n++) {
            var line = arrConfig[n];
            if (line.value) {
                config[line.value] = {value: line.value, ixOrder:n, visible: line.visible, sort: line.sort}; 
            }
        }
        return config;
    }

    /**
     * Get the items per page.
     * @param {} table 
     * @returns 
     */
    getItemsPerPage(table) {

        table = this.sanitizeTablename(table);

        let config = this.store[table] || {};
        return Number(config.itemsPerPage)||0;
    }
    
}

export default clsGlobalDatatable